import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import type { ChartData } from 'chart.js';
import { Bar, Line } from 'react-chartjs-2';
import api from '../services/authService';
import { colors } from '../styles/colors';
import Pagination from './Pagination';
import InfoTooltip from './InfoTooltip';

ChartJS.register(CategoryScale, LinearScale, BarElement, PointElement, LineElement, Title, Tooltip, Legend);

// Paleta categórica das variantes: validada para daltonismo (pior par adjacente
// ΔE 10.3 em protanopia) e contraste. A cor segue a variante em todos os gráficos
// e tabelas — nunca a posição no ranking.
const VARIANT_COLORS: { [key: string]: string } = {
  'Clone B': '#4A5FC1',
  'Fluxo A 2026': '#D9791F',
  'Fluxo B 2026': '#1FA37A',
};
const UNKNOWN_VARIANT_COLOR = '#757575';
const variantColor = (variant: string) => VARIANT_COLORS[variant] || UNKNOWN_VARIANT_COLOR;

// Dispositivo é composição, não identidade: rampa ordinal de um tom só (validada),
// pra não competir com as cores das variantes.
const DEVICE_RAMP = ['#9BA3D8', '#6E79C6', '#4A56A6', '#2C3573'];
const DEVICE_OTHER_COLOR = '#BDBDBD';
const DEVICE_LABELS: { [key: string]: string } = {
  mobile: 'Mobile', desktop: 'Desktop', tablet: 'Tablet', bot: 'Bot', desconhecido: 'Desconhecido',
};

const EXPECTED_COLOR = '#C7C7C7';
const CI_COLOR = 'rgba(33, 33, 33, 0.55)';
const GRID_COLOR = '#EEEEEE';

const STATUS_STYLES: { [key: string]: { bg: string; fg: string; icon: string } } = {
  ok: { bg: '#E8F5E9', fg: '#2E7D32', icon: '✓' },
  warning: { bg: '#FFF8E1', fg: '#B26A00', icon: '!' },
  critical: { bg: '#FFEBEE', fg: '#C62828', icon: '✕' },
  insufficient: { bg: colors.light, fg: colors.textLight, icon: '–' },
};

const VERDICT_STYLES: { [key: string]: { bg: string; fg: string; label: string } } = {
  ganha: { bg: '#E8F5E9', fg: '#2E7D32', label: 'Ganha' },
  perde: { bg: '#FFEBEE', fg: '#C62828', label: 'Perde' },
  empate: { bg: colors.light, fg: colors.textLight, label: 'Sem diferença' },
  controle: { bg: '#E8EAF6', fg: '#3949AB', label: 'Controle' },
  amostra_pequena: { bg: '#FFF8E1', fg: '#B26A00', label: 'Amostra pequena' },
  sem_controle: { bg: colors.light, fg: colors.textLight, label: '—' },
};

interface VariantRow {
  variant: string;
  redirectsTo: string;
  isControl: boolean;
  weight: number;
  visits: number;
  conversions: number;
  rate: number;
  ci: { low: number; high: number };
  share: number;
  expectedShare: number;
  uplift: number | null;
  pValue: number | null;
  significant: boolean | null;
  verdict: string;
  unexpected?: boolean;
}

interface OriginCount {
  origin: string;
  count: number;
  share: number;
}

interface Summary {
  experiment: string;
  totals: { visits: number; conversions: number; rate: number; rawVisits: number; botVisits: number; botConversions: number };
  botFilter: { visits: 'exact' | 'estimated'; conversions: 'exact' | 'none' };
  weights: { variant: string; weight: number }[];
  control: string;
  variants: VariantRow[];
  srm: { status: string; label: string; detail: string; pValue: number | null; maxDeviation: number | null };
  funnel: { variant: string; visits: number; conversions: number; publishedJob: number; conversionRate: number; publishRate: number }[];
  timeline: {
    granularity: string;
    buckets: string[];
    visits: { variant: string; data: number[] }[];
    conversions: { variant: string; data: number[] }[];
    cumulativeRate: { variant: string; data: number[] }[];
  };
  sources: { visits: OriginCount[]; conversions: OriginCount[] };
  audience: { devices: string[]; rows: { variant: string; total: number; shares: number[] }[]; isNew: number; returning: number };
  sampling: { visitsSampled: boolean; visitsRows: number; visitsTotal: number; conversionsSampled: boolean };
}

interface VisitRow {
  id: number; variant: string; redirectedTo: string; isNew: boolean; referrer: string | null;
  utmSource: string | null; utmMedium: string | null; utmCampaign: string | null;
  device: string; userAgent: string; ip: string; origin: string; createdAt: string;
}

interface JobInfo {
  title: string | null; level: string | null; type: string | null; regime: string | null;
  area: string | null; vacancies: string | null; salaryFrom: string | null; salaryTo: string | null;
  location: string | null;
}

interface ConversionRow {
  id: number; variant: string; flow: string; companyId: number | null; companyName: string | null;
  companyCnpj: string | null; companySegment: string | null; recruiterId: number | null;
  recruiterName: string | null; recruiterEmail: string; recruiterPhone: string;
  hasPublishedJob: boolean; skipOpp: boolean; opportunityId: number | null; job: JobInfo;
  utmSource: string | null; origin: string; createdAt: string;
}

const pct = (value: number, digits = 2) => `${(value * 100).toFixed(digits)}%`;
const formatDateTime = (value: string | null) =>
  (value ? new Date(value).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' }) : '—');

const formatBucket = (bucket: string, granularity: string) => {
  if (granularity === 'hour') {
    const [date, hour] = bucket.split(', ');
    const [, month, day] = (date || bucket).split('-');
    return `${day}/${month} ${hour || ''}h`;
  }
  const [, month, day] = bucket.split('-');
  return `${day}/${month}`;
};

// Mesmos valores do formulário da vaga no onboarding-front (RevisaoVaga/variables.ts)
const JOB_LEVEL_LABELS: { [key: string]: string } = { junior: 'Júnior', pleno: 'Pleno', senior: 'Sênior' };
const JOB_TYPE_LABELS: { [key: string]: string } = {
  emprego: 'Emprego', estagio: 'Estágio', freela: 'Freela', Trainee: 'Trainee', talentos: 'Banco de Talentos',
};
const JOB_REGIME_LABELS: { [key: string]: string } = { clt: 'CLT', pj: 'PJ', estagio: 'Estágio', indiferente: 'Regime indiferente' };
const JOB_LOCATION_LABELS: { [key: string]: string } = { home: 'Home Office', presencial: 'Presencial', hibrido: 'Híbrido' };

const labelFor = (labels: { [key: string]: string }, value: string | null) => (value ? labels[value] || value : null);

const formatSalary = (from: string | null, to: string | null) => {
  const money = (value: string | null) => {
    const number = Number(value);
    return value && !Number.isNaN(number) && number > 0 ? `R$ ${number.toLocaleString('pt-BR')}` : null;
  };
  const low = money(from);
  const high = money(to);

  if (low && high) return `${low} – ${high}`;
  if (low) return `a partir de ${low}`;
  if (high) return `até ${high}`;
  return null;
};

const jobSummary = (job: JobInfo) => {
  const vacancies = Number(job.vacancies);

  return [
    labelFor(JOB_LEVEL_LABELS, job.level),
    labelFor(JOB_TYPE_LABELS, job.type),
    labelFor(JOB_REGIME_LABELS, job.regime),
    labelFor(JOB_LOCATION_LABELS, job.location),
    job.area,
    vacancies > 0 ? `${vacancies} ${vacancies === 1 ? 'vaga' : 'vagas'}` : null,
    formatSalary(job.salaryFrom, job.salaryTo),
  ].filter(Boolean).join(' · ');
};

// O que cada métrica é e como é calculada. Mantenha em sincronia com as regras
// de server/controllers/onboardingAbController.js.
const metricTips = (summary: Summary) => {
  const weights = summary.weights.map(item => `${item.variant} ${item.weight}%`).join(', ');
  const control = summary.control;

  return {
    visits: 'Acessos de pessoas ao endereço inicial do onboarding que foram sorteados para uma variante. Cada redirect conta uma vez: a mesma pessoa voltando conta de novo. Acessos de robôs ficam de fora.',
    bots: 'Acessos cujo User-Agent (a identificação que o navegador envia) se declara robô: contém bot, crawler, spider, headless, preview ou monitor. Robôs que se passam por navegador comum não são detectados. Ficam fora das taxas, do teste do split, do funil e das séries, mas aparecem no gráfico de público.',
    conversions: 'Cadastros concluídos, registrados pelo front no momento em que o cadastro termina. Cadastros com User-Agent de robô ficam de fora.',
    overallRate: 'Cadastros ÷ visitas de pessoas, somando todas as variantes.',
    srm: `Confere se a divisão das visitas bate com os pesos do sorteio (${weights}) usando o teste qui-quadrado. Só avalia a partir de 100 visitas. "Split com desvio" é p < 0,01 e "Split fora do esperado" é p < 0,001: indica problema no sorteio e invalida a comparação entre variantes.`,
    result: 'Barra: taxa de conversão de cada variante. Traço escuro: intervalo de confiança de 95%. O selo de vencedor só aparece quando uma variante supera o controle com p-valor abaixo de 0,05.',
    variantVisits: 'Visitas de pessoas sorteadas para a variante (sem robôs).',
    variantConversions: 'Cadastros concluídos na variante (sem robôs).',
    rate: 'Cadastros da variante ÷ visitas de pessoas da variante.',
    ci: 'Intervalo de confiança de 95% pelo método de Wilson: a faixa onde a taxa real provavelmente está. Fica mais estreita conforme as visitas aumentam. Se as faixas de duas variantes se sobrepõem, a diferença entre elas pode ser acaso.',
    uplift: `Variação relativa da taxa em relação ao controle (${control}): (taxa da variante − taxa do controle) ÷ taxa do controle.`,
    pValue: `Chance de aparecer uma diferença igual ou maior só por acaso, se a variante e o ${control} convertessem igual. Calculado com teste z de duas proporções. Abaixo de 0,05 a diferença é considerada real. Só é calculado com pelo menos 30 visitas na variante e no controle.`,
    verdict: 'Ganha / Perde: p-valor abaixo de 0,05 contra o controle. Sem diferença: os dados ainda não distinguem a variante do controle, o que não significa que sejam iguais. Amostra pequena: menos de 30 visitas na variante ou no controle.',
    split: `Barra colorida: % das visitas de pessoas que cada variante recebeu. Barra cinza: % esperado pelos pesos do sorteio (${weights}).`,
    funnel: 'Primeira barra: cadastros ÷ visitas da variante. Segunda barra: cadastros que publicaram vaga ÷ cadastros da variante, usando o campo "publicou vaga" enviado no cadastro.',
    visitsTimeline: 'Visitas de pessoas por variante em cada dia, ou em cada hora quando o período tem até 48h. Horário de São Paulo.',
    cumulativeRate: 'Em cada ponto: cadastros acumulados até ali ÷ visitas acumuladas até ali, por variante. Mostra se a taxa está estabilizando ou ainda oscila.',
    sources: 'Visitas e cadastros por origem em listas separadas. Os dois usam regras diferentes para definir a origem, então dividir cadastros por visitas da mesma origem daria uma taxa sem sentido.',
    visitOrigin: 'Definida no momento do redirect, nesta ordem: utm_source da URL; sem UTM, clique de anúncio pelo identificador na URL (gclid → Google Ads, fbclid → Meta Ads, msclkid → Microsoft Ads); navegação interna (parâmetro cta ou _gl, ou referer trampos.co); outro site pelo referer; se nada disso, "Sem identificação".',
    conversionOrigin: 'Gravada pelo front no cadastro: primeiro o cookie de último toque (__lt_utm_source), depois o de primeiro toque (__ft_utm_source), depois a URL. Pode apontar uma campanha clicada dias antes, por isso não é comparável à origem das visitas.',
    devices: 'Classificado pelo User-Agent, nesta ordem: Bot (mesmo critério da exclusão de robôs), Tablet (iPad, Tablet, Kindle...), Mobile (iPhone, Android Mobile...), Desconhecido (sem User-Agent) e Desktop (o restante). Aqui os robôs entram, para mostrar quanto do tráfego eles representam.',
    audienceVisitors: 'Novo: chegou sem o cookie do teste e foi sorteado nesse acesso. Recorrente: já tinha o cookie e voltou para a mesma variante. Conta só pessoas.',
    visitorType: 'Novo: chegou sem o cookie do teste e foi sorteado nesse acesso. Recorrente: já tinha o cookie e voltou para a mesma variante.',
    job: 'Vaga criada no cadastro, quando a pessoa publicou. O título abre a vaga completa no Bowie. "Pulou a vaga": escolheu pular essa etapa.',
  };
};

const OnboardingAb: React.FC = () => {
  const [summary, setSummary] = useState<Summary | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [days, setDays] = useState('all');

  const [tab, setTab] = useState<'conversions' | 'visits'>('conversions');
  const [visits, setVisits] = useState<VisitRow[]>([]);
  const [conversions, setConversions] = useState<ConversionRow[]>([]);
  const [tableLoading, setTableLoading] = useState(true);
  const [variantFilter, setVariantFilter] = useState('all');
  const [revealPii, setRevealPii] = useState(false);
  const [canRevealPii, setCanRevealPii] = useState(false);
  const [page, setPage] = useState(1);
  const [tablePagination, setTablePagination] = useState({ total: 0, page: 1, limit: 20, totalPages: 0 });

  const fetchSummary = useCallback(async (force = false) => {
    try {
      if (force) setRefreshing(true);
      const params = new URLSearchParams({ days });
      if (force) params.set('refresh', '1');
      const response = await api.get(`/onboarding-ab?${params}`);
      setSummary(response.data);
      setErrorMessage(null);
    } catch (error: any) {
      setErrorMessage(error.response?.data?.error || 'Erro ao carregar dados do teste AB');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [days]);

  const fetchTable = useCallback(async () => {
    try {
      setTableLoading(true);
      const params = new URLSearchParams({ days, variant: variantFilter, page: String(page), limit: '20' });
      if (revealPii) params.set('revealPii', '1');

      const response = await api.get(`/onboarding-ab/${tab}?${params}`);
      if (tab === 'visits') setVisits(response.data.visits);
      else setConversions(response.data.conversions);

      setTablePagination(response.data.pagination);
      setCanRevealPii(response.data.canRevealPii);
      if (revealPii && !response.data.piiRevealed) setRevealPii(false);
    } catch (error: any) {
      toast.error('Erro ao carregar a tabela');
    } finally {
      setTableLoading(false);
    }
  }, [tab, days, variantFilter, page, revealPii]);

  useEffect(() => { fetchSummary(); }, [fetchSummary]);
  useEffect(() => { fetchTable(); }, [fetchTable]);
  useEffect(() => { setPage(1); }, [tab, days, variantFilter]);

  if (loading) return <div style={styles.loading}>Carregando...</div>;

  if (!summary) {
    return (
      <div style={styles.container}>
        <h1 style={styles.title}>Onboarding AB</h1>
        <div style={styles.errorBox}>
          {errorMessage}
          <div style={styles.errorHint}>
            Confira as variáveis <code style={styles.code}>AB_CONVERSIONS_READ_TOKEN</code> e
            <code style={styles.code}>ONBOARDING_METRICS_URL</code> no servidor do Bowie.
          </div>
          <button onClick={() => { setLoading(true); fetchSummary(true); }} style={styles.retryBtn}>Tentar de novo</button>
        </div>
      </div>
    );
  }

  const srmStyle = STATUS_STYLES[summary.srm.status] || STATUS_STYLES.insufficient;
  const tips = metricTips(summary);
  const variants = summary.variants;
  const winner = variants.find(row => row.verdict === 'ganha');

  // Mistura barra simples (taxa) com barra flutuante (IC), daí o tipo do dado
  const rateChart: ChartData<'bar', (number | [number, number])[], string> = {
    labels: variants.map(row => row.variant),
    datasets: [
      {
        label: 'Taxa de conversão',
        data: variants.map(row => Number((row.rate * 100).toFixed(2))),
        backgroundColor: variants.map(row => variantColor(row.variant)),
        borderRadius: 4,
        borderSkipped: false as const,
        barPercentage: 0.62,
      },
      {
        // Intervalo de confiança 95% desenhado como barra flutuante sobre a ponta
        // da barra: sem ele, diferença de ruído parece diferença real.
        label: 'IC 95%',
        data: variants.map(row => [
          Number((row.ci.low * 100).toFixed(2)),
          Number((row.ci.high * 100).toFixed(2)),
        ] as [number, number]),
        backgroundColor: CI_COLOR,
        borderRadius: 2,
        borderSkipped: false as const,
        barPercentage: 0.12,
        grouped: false,
      },
    ],
  };

  const splitChart = {
    labels: variants.map(row => row.variant),
    datasets: [
      {
        label: 'Observado',
        data: variants.map(row => Number(row.share.toFixed(2))),
        backgroundColor: variants.map(row => variantColor(row.variant)),
        borderRadius: 4,
        borderSkipped: false as const,
        barPercentage: 0.72,
      },
      {
        label: 'Esperado (peso)',
        data: variants.map(row => Number(row.expectedShare.toFixed(2))),
        backgroundColor: EXPECTED_COLOR,
        borderRadius: 4,
        borderSkipped: false as const,
        barPercentage: 0.72,
      },
    ],
  };

  const bucketLabels = summary.timeline.buckets.map(b => formatBucket(b, summary.timeline.granularity));
  const lineDataset = (serie: { variant: string; data: number[] }) => ({
    label: serie.variant,
    data: serie.data,
    borderColor: variantColor(serie.variant),
    backgroundColor: variantColor(serie.variant),
    borderWidth: 2,
    pointRadius: bucketLabels.length > 30 ? 0 : 4,
    pointHoverRadius: 6,
    tension: 0.25,
  });

  const visitsTimeChart = { labels: bucketLabels, datasets: summary.timeline.visits.map(lineDataset) };
  const rateTimeChart = { labels: bucketLabels, datasets: summary.timeline.cumulativeRate.map(lineDataset) };

  const funnelChart = {
    labels: summary.funnel.map(row => row.variant),
    datasets: [
      {
        label: 'Visita → cadastro',
        data: summary.funnel.map(row => Number((row.conversionRate * 100).toFixed(2))),
        backgroundColor: summary.funnel.map(row => variantColor(row.variant)),
        borderRadius: 4,
        borderSkipped: false as const,
        barPercentage: 0.7,
      },
      {
        label: 'Cadastro → publicou vaga',
        data: summary.funnel.map(row => Number((row.publishRate * 100).toFixed(2))),
        backgroundColor: summary.funnel.map(row => `${variantColor(row.variant)}66`),
        borderRadius: 4,
        borderSkipped: false as const,
        barPercentage: 0.7,
      },
    ],
  };

  const deviceChart = {
    labels: summary.audience.rows.map(row => row.variant),
    datasets: summary.audience.devices.map((device, index) => ({
      label: DEVICE_LABELS[device] || device,
      data: summary.audience.rows.map(row => Number(row.shares[index].toFixed(2))),
      backgroundColor: device === 'desconhecido' ? DEVICE_OTHER_COLOR : DEVICE_RAMP[index % DEVICE_RAMP.length],
      borderRadius: 4,
      borderSkipped: false as const,
      borderColor: colors.white,
      borderWidth: 2,
      barPercentage: 0.6,
    })),
  };

  const percentAxis = {
    grid: { color: GRID_COLOR },
    ticks: { callback: (value: any) => `${value}%` },
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Onboarding AB</h1>
          <div style={styles.subtitle}>
            Experimento <strong>{summary.experiment}</strong> · controle: <strong>{summary.control}</strong>
          </div>
        </div>
        <div style={styles.headerActions}>
          <select value={days} onChange={(e) => setDays(e.target.value)} style={styles.select}>
            <option value="all">Todo o período</option>
            <option value="1">Últimas 24h</option>
            <option value="7">Últimos 7 dias</option>
            <option value="30">Últimos 30 dias</option>
          </select>
          <button onClick={() => fetchSummary(true)} disabled={refreshing} style={styles.refreshBtn}>
            {refreshing ? 'Atualizando...' : 'Atualizar'}
          </button>
        </div>
      </div>

      {summary.sampling.visitsSampled && (
        <div style={styles.warningBanner}>
          ⚠️ O período tem {summary.sampling.visitsTotal.toLocaleString('pt-BR')} visitas e só as
          {' '}{summary.sampling.visitsRows.toLocaleString('pt-BR')} mais recentes foram analisadas uma a uma.
          Por isso os gráficos de série temporal, origem e público usam essa amostra, e a exclusão de
          robôs nos totais e taxas é estimada pela proporção de robôs na amostra. Filtre um período
          menor para ter tudo exato.
        </div>
      )}

      <div style={styles.kpiRow}>
        <div style={styles.kpiCard}>
          <div style={styles.kpiValue}>{summary.totals.visits.toLocaleString('pt-BR')}</div>
          <div style={styles.kpiLabel}>
            visitas de pessoas <InfoTooltip label="Visitas" text={tips.visits} />
          </div>
        </div>
        <div style={styles.kpiCard}>
          <div style={styles.kpiValue}>{summary.totals.botVisits.toLocaleString('pt-BR')}</div>
          <div style={styles.kpiLabel}>
            acessos de robôs excluídos
            {summary.totals.rawVisits > 0 && ` · ${Math.round((summary.totals.botVisits / summary.totals.rawVisits) * 100)}% do total`}
            {summary.botFilter.visits === 'estimated' && ' (estimado)'}
            <InfoTooltip label="Robôs" text={tips.bots} />
          </div>
        </div>
        <div style={styles.kpiCard}>
          <div style={styles.kpiValue}>{summary.totals.conversions.toLocaleString('pt-BR')}</div>
          <div style={styles.kpiLabel}>
            cadastros concluídos <InfoTooltip label="Cadastros" text={tips.conversions} />
          </div>
        </div>
        <div style={styles.kpiCard}>
          <div style={styles.kpiValue}>{pct(summary.totals.rate)}</div>
          <div style={styles.kpiLabel}>
            taxa de conversão geral <InfoTooltip label="Taxa de conversão geral" text={tips.overallRate} />
          </div>
        </div>
        <div style={{ ...styles.kpiCard, backgroundColor: srmStyle.bg, borderColor: srmStyle.fg }}>
          <div style={{ ...styles.statusLabel, color: srmStyle.fg }}>
            <span style={styles.statusIcon}>{srmStyle.icon}</span> {summary.srm.label}
            <InfoTooltip label="Teste do split" text={tips.srm} />
          </div>
          <div style={styles.kpiLabel}>
            {summary.srm.maxDeviation !== null
              ? `desvio máximo de ${summary.srm.maxDeviation.toFixed(1)}pp no split`
              : 'sem dados suficientes'}
          </div>
        </div>
      </div>

      <div style={styles.chartCardWide}>
        <div style={styles.resultHeader}>
          <div>
            <h3 style={styles.chartTitle}>
              Resultado do teste <InfoTooltip label="Resultado do teste" text={tips.result} />
            </h3>
            <p style={styles.chartSubtitle}>
              Taxa de conversão por variante com intervalo de confiança de 95%. Barras que se
              sobrepõem no intervalo não são diferença comprovada.
            </p>
          </div>
          {winner ? (
            <div style={{ ...styles.winnerBadge, backgroundColor: VERDICT_STYLES.ganha.bg, color: VERDICT_STYLES.ganha.fg }}>
              🏆 {winner.variant} vence · {winner.uplift! > 0 ? '+' : ''}{winner.uplift!.toFixed(1)}% vs controle
            </div>
          ) : (
            <div style={{ ...styles.winnerBadge, backgroundColor: colors.light, color: colors.textLight }}>
              Nenhuma variante venceu ainda
            </div>
          )}
        </div>

        <div style={styles.chartBox}>
          <Bar
            data={rateChart}
            options={{
              indexAxis: 'y' as const,
              responsive: true,
              maintainAspectRatio: false,
              plugins: {
                legend: { display: false },
                tooltip: {
                  callbacks: {
                    label: (ctx: any) => (Array.isArray(ctx.raw)
                      ? `IC 95%: ${ctx.raw[0].toFixed(2)}% – ${ctx.raw[1].toFixed(2)}%`
                      : `Taxa: ${Number(ctx.raw).toFixed(2)}%`),
                  },
                },
              },
              scales: { x: { beginAtZero: true, ...percentAxis }, y: { grid: { display: false } } },
            }}
          />
        </div>

        <div style={styles.tableScroll}>
          <table style={styles.dataTable}>
            <thead>
              <tr>
                <th style={styles.th}>Variante</th>
                <th style={styles.thRight}>Visitas <InfoTooltip label="Visitas" text={tips.variantVisits} /></th>
                <th style={styles.thRight}>Cadastros <InfoTooltip label="Cadastros" text={tips.variantConversions} /></th>
                <th style={styles.thRight}>Taxa <InfoTooltip label="Taxa" text={tips.rate} /></th>
                <th style={styles.thRight}>IC 95% <InfoTooltip label="Intervalo de confiança" text={tips.ci} /></th>
                <th style={styles.thRight}>vs controle <InfoTooltip label="Variação contra o controle" text={tips.uplift} /></th>
                <th style={styles.thRight}>p-valor <InfoTooltip label="p-valor" text={tips.pValue} /></th>
                <th style={styles.th}>Veredito <InfoTooltip label="Veredito" text={tips.verdict} /></th>
              </tr>
            </thead>
            <tbody>
              {variants.map(row => {
                const verdict = VERDICT_STYLES[row.verdict] || VERDICT_STYLES.sem_controle;
                return (
                  <tr key={row.variant} style={styles.tr}>
                    <td style={styles.td}>
                      <span style={{ ...styles.swatch, backgroundColor: variantColor(row.variant) }} />
                      {row.variant}
                      {row.unexpected && <span style={styles.unexpectedTag}>não configurada</span>}
                      <div style={styles.routeHint}>{row.redirectsTo} · peso {row.weight}%</div>
                    </td>
                    <td style={styles.tdRight}>{row.visits.toLocaleString('pt-BR')}</td>
                    <td style={styles.tdRight}>{row.conversions.toLocaleString('pt-BR')}</td>
                    <td style={styles.tdRightStrong}>{pct(row.rate)}</td>
                    <td style={styles.tdRightMuted}>{pct(row.ci.low, 1)} – {pct(row.ci.high, 1)}</td>
                    <td style={styles.tdRight}>
                      {row.uplift === null ? '—' : `${row.uplift > 0 ? '+' : ''}${row.uplift.toFixed(1)}%`}
                    </td>
                    <td style={styles.tdRightMuted}>
                      {row.pValue === null ? '—' : row.pValue < 0.0001 ? '<0,0001' : row.pValue.toFixed(4)}
                    </td>
                    <td style={styles.td}>
                      <span style={{ ...styles.verdictBadge, backgroundColor: verdict.bg, color: verdict.fg }}>
                        {verdict.label}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <p style={styles.footnoteInline}>
          Veredito usa teste z de duas proporções contra o controle, significância em p &lt; 0,05.
          "Sem diferença" quer dizer que os dados ainda não distinguem a variante do controle — não que sejam iguais.
        </p>
      </div>

      <div style={styles.chartsRow}>
        <div style={styles.chartCard}>
          <h3 style={styles.chartTitle}>
            Divisão de tráfego <InfoTooltip label="Divisão de tráfego" text={tips.split} />
          </h3>
          <p style={styles.chartSubtitle}>{summary.srm.detail}</p>
          <div style={styles.chartBoxShort}>
            <Bar
              data={splitChart}
              options={{
                indexAxis: 'y' as const,
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: { display: false },
                  tooltip: { callbacks: { label: (ctx: any) => `${ctx.dataset.label}: ${Number(ctx.raw).toFixed(1)}%` } },
                },
                scales: { x: { beginAtZero: true, ...percentAxis }, y: { grid: { display: false } } },
              }}
            />
          </div>
          <div style={styles.inlineLegend}>
            <span><span style={{ ...styles.swatch, backgroundColor: colors.textLight }} />barra clara = esperado pelo peso</span>
          </div>
        </div>

        <div style={styles.chartCard}>
          <h3 style={styles.chartTitle}>
            Funil por variante <InfoTooltip label="Funil" text={tips.funnel} />
          </h3>
          <p style={styles.chartSubtitle}>
            Converter mais não basta: a segunda barra mostra quantos dos cadastros chegaram a publicar vaga.
          </p>
          <div style={styles.chartBoxShort}>
            <Bar
              data={funnelChart}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: { position: 'bottom' as const, labels: { usePointStyle: true, boxWidth: 8 } },
                  tooltip: { callbacks: { label: (ctx: any) => `${ctx.dataset.label}: ${Number(ctx.raw).toFixed(2)}%` } },
                },
                scales: { x: { grid: { display: false } }, y: { beginAtZero: true, ...percentAxis } },
              }}
            />
          </div>
          <div style={styles.funnelNumbers}>
            {summary.funnel.map(row => (
              <div key={row.variant} style={styles.funnelRow}>
                <span style={{ ...styles.swatch, backgroundColor: variantColor(row.variant) }} />
                <strong>{row.variant}:</strong>&nbsp;
                {row.visits.toLocaleString('pt-BR')} visitas → {row.conversions} cadastros → {row.publishedJob} publicaram
              </div>
            ))}
          </div>
        </div>
      </div>

      <div style={styles.chartsRow}>
        <div style={styles.chartCard}>
          <h3 style={styles.chartTitle}>
            Visitas por {summary.timeline.granularity === 'hour' ? 'hora' : 'dia'}
            <InfoTooltip label="Visitas ao longo do tempo" text={tips.visitsTimeline} />
          </h3>
          <p style={styles.chartSubtitle}>Visitas de pessoas de cada variante ao longo do tempo, sem robôs.</p>
          <div style={styles.chartBoxShort}>
            <Line
              data={visitsTimeChart}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                interaction: { mode: 'index' as const, intersect: false },
                plugins: { legend: { position: 'bottom' as const, labels: { usePointStyle: true, boxWidth: 8 } } },
                scales: { x: { grid: { display: false } }, y: { beginAtZero: true, grid: { color: GRID_COLOR } } },
              }}
            />
          </div>
        </div>

        <div style={styles.chartCard}>
          <h3 style={styles.chartTitle}>
            Taxa de conversão acumulada <InfoTooltip label="Taxa acumulada" text={tips.cumulativeRate} />
          </h3>
          <p style={styles.chartSubtitle}>
            Acumulada, não diária: com volume baixo a taxa do dia oscila muito e engana. Aqui dá pra
            ver se a diferença está estabilizando ou ainda é ruído.
          </p>
          <div style={styles.chartBoxShort}>
            <Line
              data={rateTimeChart}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                interaction: { mode: 'index' as const, intersect: false },
                plugins: {
                  legend: { position: 'bottom' as const, labels: { usePointStyle: true, boxWidth: 8 } },
                  tooltip: { callbacks: { label: (ctx: any) => `${ctx.dataset.label}: ${Number(ctx.raw).toFixed(2)}%` } },
                },
                scales: { x: { grid: { display: false } }, y: { beginAtZero: true, ...percentAxis } },
              }}
            />
          </div>
        </div>
      </div>

      <div style={styles.chartsRow}>
        <div style={styles.chartCard}>
          <h3 style={styles.chartTitle}>
            Origem <InfoTooltip label="Origem" text={tips.sources} />
          </h3>
          <p style={styles.chartSubtitle}>
            Visitas e cadastros definem a origem por regras diferentes, por isso ficam lado a lado e
            sem taxa por origem. Top 10 de cada.
          </p>
          <div style={styles.originColumns}>
            {[
              { title: 'Visitas', tip: tips.visitOrigin, rows: summary.sources.visits },
              { title: 'Cadastros', tip: tips.conversionOrigin, rows: summary.sources.conversions },
            ].map(column => (
              <div key={column.title} style={styles.originColumn}>
                <table style={styles.dataTable}>
                  <thead>
                    <tr>
                      <th style={styles.th}>
                        {column.title} por origem <InfoTooltip label={`${column.title} por origem`} text={column.tip} />
                      </th>
                      <th style={styles.thRight}>Qtd</th>
                      <th style={styles.thRight}>%</th>
                    </tr>
                  </thead>
                  <tbody>
                    {column.rows.length === 0 ? (
                      <tr><td colSpan={3} style={styles.tdMuted}>Nenhum registro</td></tr>
                    ) : column.rows.map(row => (
                      <tr key={row.origin} style={styles.tr}>
                        <td style={styles.td}>{row.origin}</td>
                        <td style={styles.tdRight}>{row.count.toLocaleString('pt-BR')}</td>
                        <td style={styles.tdRightMuted}>{row.share.toFixed(0)}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        </div>

        <div style={styles.chartCard}>
          <h3 style={styles.chartTitle}>
            Público por variante <InfoTooltip label="Público por variante" text={tips.devices} />
          </h3>
          <p style={styles.chartSubtitle}>
            Composição parecida entre variantes indica sorteio limpo. Bot inflando uma delas distorce a taxa.
          </p>
          <div style={styles.chartBoxShort}>
            <Bar
              data={deviceChart}
              options={{
                indexAxis: 'y' as const,
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: { position: 'bottom' as const, labels: { usePointStyle: true, boxWidth: 8 } },
                  tooltip: { callbacks: { label: (ctx: any) => `${ctx.dataset.label}: ${Number(ctx.raw).toFixed(1)}%` } },
                },
                scales: {
                  x: { stacked: true, max: 100, ...percentAxis },
                  y: { stacked: true, grid: { display: false } },
                },
              }}
            />
          </div>
          <div style={styles.inlineLegend}>
            <span>Novos: <strong>{summary.audience.isNew.toLocaleString('pt-BR')}</strong></span>
            <span>
              Recorrentes: <strong>{summary.audience.returning.toLocaleString('pt-BR')}</strong>
              <InfoTooltip label="Novos e recorrentes" text={tips.audienceVisitors} />
            </span>
          </div>
        </div>
      </div>

      <div style={styles.tableCard}>
        <div style={styles.tableHeaderRow}>
          <div style={styles.tabs}>
            <button
              onClick={() => setTab('conversions')}
              style={{ ...styles.tab, ...(tab === 'conversions' ? styles.tabActive : {}) }}
            >
              Cadastros
            </button>
            <button
              onClick={() => setTab('visits')}
              style={{ ...styles.tab, ...(tab === 'visits' ? styles.tabActive : {}) }}
            >
              Visitas
            </button>
          </div>
          {canRevealPii && (
            <label style={styles.revealLabel}>
              <input type="checkbox" checked={revealPii} onChange={(e) => setRevealPii(e.target.checked)} style={styles.checkbox} />
              Mostrar dados pessoais completos
            </label>
          )}
        </div>

        <div style={styles.filters}>
          <select value={variantFilter} onChange={(e) => setVariantFilter(e.target.value)} style={styles.select}>
            <option value="all">Todas as variantes</option>
            {variants.map(row => <option key={row.variant} value={row.variant}>{row.variant}</option>)}
          </select>
        </div>

        {tableLoading ? (
          <div style={styles.loading}>Carregando...</div>
        ) : (tab === 'conversions' ? conversions.length === 0 : visits.length === 0) ? (
          <div style={styles.emptyBox}>Nenhum registro para esses filtros.</div>
        ) : (
          <>
            <div style={styles.tableScroll}>
              {tab === 'conversions' ? (
                <table style={styles.dataTable}>
                  <thead>
                    <tr>
                      <th style={styles.th}>Data</th>
                      <th style={styles.th}>Variante</th>
                      <th style={styles.th}>Empresa</th>
                      <th style={styles.th}>Recrutador</th>
                      <th style={styles.th}>Contato</th>
                      <th style={styles.th}>Vaga <InfoTooltip label="Vaga" text={tips.job} /></th>
                      <th style={styles.th}>Origem <InfoTooltip label="Origem do cadastro" text={tips.conversionOrigin} /></th>
                    </tr>
                  </thead>
                  <tbody>
                    {conversions.map(row => (
                      <tr key={row.id} style={styles.tr}>
                        <td style={styles.td}>{formatDateTime(row.createdAt)}</td>
                        <td style={styles.td}>
                          <span style={{ ...styles.swatch, backgroundColor: variantColor(row.variant) }} />
                          {row.variant}
                        </td>
                        <td style={styles.td}>
                          {row.companyId ? (
                            <a href={`/companies?companyId=${row.companyId}`} target="_blank" rel="noreferrer" style={styles.link}>
                              {row.companyName || `#${row.companyId}`}
                            </a>
                          ) : (row.companyName || '—')}
                          <div style={styles.routeHint}>{row.companySegment || ''}</div>
                        </td>
                        <td style={styles.td}>{row.recruiterName || '—'}</td>
                        <td style={styles.tdMuted}>
                          {row.recruiterEmail || '—'}
                          <div style={styles.routeHint}>{row.recruiterPhone}</div>
                        </td>
                        <td style={styles.td}>
                          {row.hasPublishedJob ? (
                            <>
                              {row.opportunityId ? (
                                <a href={`/opportunities/${row.opportunityId}/details`} target="_blank" rel="noreferrer" style={styles.link}>
                                  {row.job.title || `Vaga #${row.opportunityId}`}
                                </a>
                              ) : (
                                <span style={styles.publishedTag}>{row.job.title || 'publicou'}</span>
                              )}
                              <div style={styles.routeHint}>{jobSummary(row.job)}</div>
                            </>
                          ) : (
                            <span style={styles.notPublishedTag}>{row.skipOpp ? 'pulou a vaga' : 'não publicou'}</span>
                          )}
                        </td>
                        <td style={styles.tdMuted}>{row.origin}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <table style={styles.dataTable}>
                  <thead>
                    <tr>
                      <th style={styles.th}>Data</th>
                      <th style={styles.th}>Variante</th>
                      <th style={styles.th}>Destino</th>
                      <th style={styles.th}>Visitante <InfoTooltip label="Visitante" text={tips.visitorType} /></th>
                      <th style={styles.th}>Dispositivo <InfoTooltip label="Dispositivo" text={tips.devices} /></th>
                      <th style={styles.th}>Origem <InfoTooltip label="Origem da visita" text={tips.visitOrigin} /></th>
                      <th style={styles.th}>IP</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visits.map(row => (
                      <tr key={row.id} style={styles.tr}>
                        <td style={styles.td}>{formatDateTime(row.createdAt)}</td>
                        <td style={styles.td}>
                          <span style={{ ...styles.swatch, backgroundColor: variantColor(row.variant) }} />
                          {row.variant}
                        </td>
                        <td style={styles.tdMuted}>{row.redirectedTo}</td>
                        <td style={styles.td}>{row.isNew ? 'Novo' : 'Recorrente'}</td>
                        <td style={styles.td}>{DEVICE_LABELS[row.device] || row.device}</td>
                        <td style={styles.tdMuted}>
                          {row.origin}
                          <div style={styles.routeHint}>{[row.utmMedium, row.utmCampaign].filter(Boolean).join(' · ')}</div>
                        </td>
                        <td style={styles.tdMono}>{row.ip}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            <div style={styles.paginationContainer}>
              <div style={styles.paginationInfo}>
                Página {tablePagination.page} de {tablePagination.totalPages} ({tablePagination.total.toLocaleString('pt-BR')} registros)
              </div>
              <Pagination
                currentPage={tablePagination.page}
                totalPages={tablePagination.totalPages}
                onPageChange={setPage}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: { width: '100%' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.25rem', gap: '1rem', flexWrap: 'wrap' },
  title: { fontSize: '2rem', color: colors.primary, margin: 0 },
  subtitle: { color: colors.textLight, fontSize: '0.9rem', marginTop: '0.35rem' },
  headerActions: { display: 'flex', gap: '0.75rem', alignItems: 'center' },
  select: { padding: '0.6rem', border: `1px solid ${colors.border}`, borderRadius: '4px', fontSize: '0.95rem', fontFamily: 'inherit', backgroundColor: colors.white },
  refreshBtn: { padding: '0.6rem 1.25rem', backgroundColor: colors.primary, color: colors.white, border: 'none', borderRadius: '4px', cursor: 'pointer', fontFamily: 'inherit', fontSize: '0.95rem' },
  warningBanner: { padding: '0.75rem 1rem', backgroundColor: '#FFF8E1', border: '1px solid #FFE082', borderRadius: '4px', color: '#7A5B00', fontSize: '0.85rem', marginBottom: '1.5rem' },
  kpiRow: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '1.5rem' },
  kpiCard: { backgroundColor: colors.white, border: `1px solid ${colors.border}`, borderRadius: '8px', padding: '1.1rem 1.25rem', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' },
  kpiValue: { fontSize: '2rem', fontWeight: 'bold', color: colors.primary, lineHeight: 1.1 },
  kpiLabel: { fontSize: '0.82rem', color: colors.textLight, marginTop: '0.35rem' },
  statusLabel: { fontSize: '1.15rem', fontWeight: 'bold', lineHeight: 1.2 },
  statusIcon: { fontWeight: 'bold', marginRight: '0.25rem' },
  chartsRow: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(430px, 1fr))', gap: '1rem', marginBottom: '1.5rem' },
  chartCard: { backgroundColor: colors.white, border: `1px solid ${colors.border}`, borderRadius: '8px', padding: '1.25rem', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' },
  chartCardWide: { backgroundColor: colors.white, border: `1px solid ${colors.border}`, borderRadius: '8px', padding: '1.25rem', boxShadow: '0 2px 4px rgba(0,0,0,0.05)', marginBottom: '1.5rem' },
  resultHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem', flexWrap: 'wrap' },
  winnerBadge: { padding: '0.6rem 1.1rem', borderRadius: '20px', fontWeight: 'bold', fontSize: '0.95rem', whiteSpace: 'nowrap' },
  chartTitle: { margin: 0, fontSize: '1.1rem', color: colors.text },
  chartSubtitle: { margin: '0.3rem 0 1rem 0', fontSize: '0.82rem', color: colors.textLight, maxWidth: '620px' },
  chartBox: { height: '230px', marginBottom: '1rem' },
  chartBoxShort: { height: '215px' },
  inlineLegend: { display: 'flex', gap: '1.25rem', marginTop: '0.75rem', fontSize: '0.8rem', color: colors.textLight },
  funnelNumbers: { marginTop: '0.85rem', fontSize: '0.82rem', color: colors.textLight },
  funnelRow: { marginBottom: '0.25rem' },
  originColumns: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: '1rem' },
  originColumn: { overflowX: 'auto' },
  tableScroll: { overflowX: 'auto' },
  dataTable: { width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' },
  th: { textAlign: 'left', padding: '0.6rem 0.5rem', color: colors.textLight, fontWeight: 600, borderBottom: `1px solid ${colors.border}`, whiteSpace: 'nowrap' },
  thRight: { textAlign: 'right', padding: '0.6rem 0.5rem', color: colors.textLight, fontWeight: 600, borderBottom: `1px solid ${colors.border}`, whiteSpace: 'nowrap' },
  tr: { borderBottom: `1px solid ${colors.borderLight}` },
  td: { padding: '0.6rem 0.5rem', color: colors.text, verticalAlign: 'top' },
  tdRight: { padding: '0.6rem 0.5rem', color: colors.text, textAlign: 'right', whiteSpace: 'nowrap' },
  tdRightStrong: { padding: '0.6rem 0.5rem', color: colors.primary, textAlign: 'right', fontWeight: 'bold', whiteSpace: 'nowrap' },
  tdRightMuted: { padding: '0.6rem 0.5rem', color: colors.textLight, textAlign: 'right', whiteSpace: 'nowrap' },
  tdMuted: { padding: '0.6rem 0.5rem', color: colors.textLight, verticalAlign: 'top' },
  tdMono: { padding: '0.6rem 0.5rem', color: colors.text, fontFamily: 'monospace', whiteSpace: 'nowrap' },
  swatch: { display: 'inline-block', width: '10px', height: '10px', borderRadius: '2px', marginRight: '0.45rem' },
  routeHint: { fontSize: '0.72rem', color: colors.textMuted, marginTop: '0.15rem' },
  verdictBadge: { padding: '0.2rem 0.6rem', borderRadius: '12px', fontSize: '0.75rem', fontWeight: 'bold', whiteSpace: 'nowrap' },
  unexpectedTag: { marginLeft: '0.4rem', fontSize: '0.7rem', color: '#C62828', border: '1px solid #C62828', borderRadius: '3px', padding: '0 0.25rem' },
  publishedTag: { color: '#2E7D32', fontSize: '0.8rem' },
  notPublishedTag: { color: colors.textMuted, fontSize: '0.8rem' },
  footnoteInline: { marginTop: '0.85rem', fontSize: '0.78rem', color: colors.textMuted },
  tableCard: { backgroundColor: colors.white, border: `1px solid ${colors.border}`, borderRadius: '8px', padding: '1.25rem', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' },
  tableHeaderRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.75rem' },
  tabs: { display: 'flex', gap: '0.5rem' },
  tab: { padding: '0.5rem 1.1rem', backgroundColor: colors.white, color: colors.text, border: `1px solid ${colors.border}`, borderRadius: '4px', cursor: 'pointer', fontFamily: 'inherit', fontSize: '0.9rem' },
  tabActive: { backgroundColor: colors.primary, color: colors.white, borderColor: colors.primary },
  revealLabel: { fontSize: '0.85rem', color: colors.textLight, display: 'flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer' },
  checkbox: { cursor: 'pointer' },
  filters: { display: 'flex', gap: '0.75rem', marginBottom: '1rem', flexWrap: 'wrap' },
  link: { color: colors.primary, textDecoration: 'none' },
  paginationContainer: { marginTop: '1.25rem' },
  paginationInfo: { textAlign: 'center', color: colors.textLight, marginBottom: '0.75rem', fontSize: '0.85rem' },
  emptyBox: { padding: '2rem', textAlign: 'center', color: colors.textLight, backgroundColor: colors.background, borderRadius: '4px' },
  errorBox: { padding: '1.5rem', backgroundColor: '#FFEBEE', border: '1px solid #FFCDD2', borderRadius: '4px', color: '#C62828' },
  errorHint: { marginTop: '0.6rem', fontSize: '0.85rem' },
  code: { backgroundColor: colors.white, padding: '0.1rem 0.35rem', borderRadius: '3px', fontFamily: 'monospace', margin: '0 0.2rem' },
  retryBtn: { display: 'block', marginTop: '0.75rem', padding: '0.5rem 1.25rem', backgroundColor: colors.primary, color: colors.white, border: 'none', borderRadius: '4px', cursor: 'pointer', fontFamily: 'inherit' },
  loading: { textAlign: 'center', padding: '2rem', fontSize: '1.1rem', color: colors.textLight },
};

export default OnboardingAb;
